import Loading from "../../../components/core/blocks/Loading";
import { useFetch } from "../../../hooks/api";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Table } from "react-bootstrap";
import api from "../../../utils/api";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../../../components/core/basic/Button";
import AddCompetitor, { RemoveCompetitor } from "../AddCompetitor";
import { formatCurrency } from "../../../utils/formatNumber";
import ListingDetails from "../components/ListingDetails";
import { cut } from "../../../utils/formatText";
import ExternalProductLink from "../../../components/custom/merchandise/ExternalProductLink";
import { SimpleCan, useSimpleCan } from "../../../config/casl/casl";

const CompetitorButtons = ({ setShowAddCompetitor }) => {
  const { t } = useTranslation(["pricingv2"]);
  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");
  return (
    <Row className="py-4 ps-3">
      <Col xs={6} className="text-start">
        <Button
          onPress={() => {
            setShowAddCompetitor(true);
          }}
          isDisabled={!userCanUpdatePricingPlans}
        >
          {t(($) => $.pricingv2["Add Competitor"])}
        </Button>
      </Col>
    </Row>
  );
};

const CompetitorsSection = ({ productId, marketPlace }) => {
  const { t } = useTranslation(["pricingv2"]);
  const [thisItem, setThisItem] = useState(null);
  const [showContentDetails, setShowContentDetails] = useState(false);
  const [showAddCompetitor, setShowAddCompetitor] = useState(false);
  const [showRemoveCompetitor, setShowRemoveCompetitor] = useState(false);
  const [actionId, setActionId] = useState(null);

  const key = ["ProductCompetitors", productId];
  const queryClient = useQueryClient();

  const { data: competitors, isLoading: isAllCompetitorsLoading } = useFetch(
    key,
    "/api/competitors",
    {
      marketplace: marketPlace.marketPlace,
      product: productId,
    },
    { enabled: !!productId },
  );

  const createCompetitor = async (productId, url) => {
    const res = await api.post("/api/competitors/", {
      product: productId,
      marketplace: marketPlace.marketPlace,
      url,
    });
    if (res.status === 201) {
      queryClient.invalidateQueries({
        queryKey: key,
      });
      setShowAddCompetitor(false);
    }
  };

  const deleteCompetitor = async (id) => {
    setShowRemoveCompetitor(false);

    const res = await api.remove(`/api/competitors/${id}/`, {});

    if (res.status === 204) {
      queryClient.invalidateQueries({
        queryKey: key,
      });
    }
  };

  if (isAllCompetitorsLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  const getBestSellerRank = (c) => {
    if (c?.history?.length === 0) {
      return t(($) => $.pricingv2["n/a"]);
    }

    let rank = 0;
    for (let i = 0; i < c.history.length; i++) {
      const h = c.history[i];
      if (h.bestseller_rank && h.bestseller_rank.length > 0) {
        for (let j = 0; j < h.bestseller_rank.length; j++) {
          const b = h.bestseller_rank[j];
          rank = b.rank;
        }
      }
    }

    if (rank === 0) {
      return t(($) => $.pricingv2["n/a"]);
    }

    return rank;
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <CompetitorButtons setShowAddCompetitor={setShowAddCompetitor} />
          {competitors && competitors.data.results.length === 0 && (
            <Row className="py-5">
              <Col xs={12}>
                <div className="text-center">
                  <p>{t(($) => $.pricingv2["You have not yet added a competitor"])}</p>
                </div>
              </Col>
            </Row>
          )}
          {competitors && competitors.data.results.length > 0 && (
            <Row className="pb-3">
              <Col xs={12}>
                <div className="table-responsive" style={{ maxHeight: "350px", overflowY: "scroll" }}>
                  <Table className="table table-striped">
                    <thead>
                      <tr>
                        <th>&nbsp;</th>
                        <th className="text-start">{t(($) => $.pricingv2["ASIN"])}</th>
                        <th className="text-start">{t(($) => $.pricingv2["Brand"])}</th>
                        <th className="text-start">{t(($) => $.pricingv2["Title"])}</th>
                        <th className="text-end">{t(($) => $.pricingv2["Price"])}</th>
                        <th className="text-center">{t(($) => $.pricingv2["BSR"])}</th>
                        <th>&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competitors.data.results.map((c, index) => (
                        <tr key={index}>
                          <td>
                            <img src={c.image_url} alt={c.title} style={{ width: "50px" }} />
                          </td>
                          <td className="text-start">
                            {c.product_ref}
                            <ExternalProductLink asin={c.product_ref} />
                          </td>
                          <td className="text-start">
                            {c?.history?.length > 0 && (
                              <span>
                                {" "}
                                <a
                                  href={`https://www.amazon.com/s?k=${c.history[c.history.length - 1].brand}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {c.history[c.history.length - 1].brand}
                                </a>
                              </span>
                            )}
                          </td>
                          <td className="text-start">{cut(c.title, 75)}</td>
                          <td className="text-end">
                            {c?.history?.length > 0 && (
                              <span>
                                {formatCurrency(c.history[c.history.length - 1].price, {
                                  marketPlace: marketPlace.marketPlace,
                                })}
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            {c?.history?.length > 0 && <span>{getBestSellerRank(c)}</span>}
                          </td>
                          <td className="text-end">
                            <SimpleCan subject="PricingPlan" action="update">
                              <select
                                onChange={(e) => {
                                  if (e.target.value === "select") {
                                    return;
                                  } else if (e.target.value === "details") {
                                    const item = {
                                      asin: c.product_ref,
                                      price: c.history[c.history.length - 1].price,
                                    };
                                    setThisItem(item);
                                    setShowContentDetails(true);
                                  } else if (e.target.value === "compare") {
                                    return;
                                  } else if (e.target.value === "remove") {
                                    setActionId(c.id);
                                    setShowRemoveCompetitor(true);
                                  }
                                }}
                                style={{
                                  maxWidth: "100px",
                                  padding: "3px",
                                  border: `"1px solid #ccc"`,
                                  borderRadius: "5px",
                                }}
                              >
                                <option value="select">{t(($) => $.pricingv2["Select"])}</option>
                                <option value="details">{t(($) => $.pricingv2["View Details"])}</option>
                                <option value="remove">{t(($) => $.pricingv2["Remove as Competitor"])}</option>
                              </select>
                            </SimpleCan>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <AddCompetitor
        productId={productId}
        createCompetitor={createCompetitor}
        setShowAddCompetitor={setShowAddCompetitor}
        showAddCompetitor={showAddCompetitor}
      />
      <RemoveCompetitor
        deleteCompetitor={deleteCompetitor}
        id={actionId}
        setShowRemoveCompetitor={setShowRemoveCompetitor}
        showRemoveCompetitor={showRemoveCompetitor}
      />
      <ListingDetails
        productID={null}
        item={thisItem}
        marketPlace={marketPlace.marketPlace}
        setShowContentDetails={setShowContentDetails}
        showContentDetails={showContentDetails}
      />
    </>
  );
};

export default CompetitorsSection;
